import {
  VStack,
  FormControl,
  FormLabel,
  HStack,
  FormErrorMessage,
} from "@chakra-ui/react";
import { capitalize } from "lodash";
import {
  RECURRING_FREQUENCIES,
  PERIODIC_FREQUENCIES,
} from "../../../../common/constants/campaign";
import { FormType } from "../../../../common/types/campaign";
import InputFieldWithError from "../../../../components/InputFieldWithError";
import DropdownWithSearch from "../../../../components/DropdownWithSearch";
import ITitle from "../../../../components/ITitle";
import { getShortTimezoneName } from "../../../../common/helper/commonHelper";

function StartDateInput({ form }: { form: FormType }) {
  return (
    <InputFieldWithError
      labelText="Schedule start date"
      value={form.values.run_config.start_date || ""}
      errorMsg={form.errors.run_config?.start_date}
      formLabelProps={{ color: "gray.500", fontSize: "xs" }}
      name="run_config.start_date"
      onChange={form.handleChange}
      onBlur={form.handleBlur}
      type="date"
      min={new Date().toISOString().slice(0, 10)}
      max={
        form.values.run_config.end_date
          ? new Date(form.values.run_config.end_date).toISOString().slice(0, 10)
          : undefined
      }
    />
  );
}

function EndDateInput({ form }: { form: FormType }) {
  const limit = form.values.run_config.start_date
    ? new Date(form.values.run_config.start_date)
    : new Date();
  const limitDate = limit.toISOString().slice(0, 10);
  return (
    <InputFieldWithError
      labelText="Schedule end date"
      value={form.values.run_config.end_date || ""}
      errorMsg={form.errors.run_config?.end_date}
      name="run_config.end_date"
      onChange={form.handleChange}
      onBlur={form.handleBlur}
      type="date"
      formLabelProps={{ color: "gray.500", fontSize: "xs" }}
      min={limitDate}
    />
  );
}

function TimeInput({ form }: { form: FormType }) {
  return (
    <InputFieldWithError
      capitalize={false}
      labelText={`Schedule first run (${getShortTimezoneName()})`}
      value={form.values.run_config.run_time || ""}
      errorMsg={form.errors.run_config?.run_time}
      name="run_config.run_time"
      onChange={form.handleChange}
      onBlur={form.handleBlur}
      type="time"
      formLabelProps={{ color: "gray.500", fontSize: "xs" }}
    />
  );
}

function WeekPicker({ form }: { form: FormType }) {
  const days = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const options = days.map((freq) => {
    return { label: capitalize(freq), value: freq };
  });

  return (
    <FormControl isInvalid={!!form.errors.run_config?.freq_options?.day}>
      <FormLabel color="gray.500" fontSize="xs">
        Run every
      </FormLabel>
      <DropdownWithSearch
        options={options}
        value={options.filter(
          (freq) => freq.value === form.values.run_config.freq_options.day
        )}
        onChange={(option) =>
          form.setFieldValue("run_config.freq_options.day", option?.value ?? "")
        }
        isInvalid={!!form.errors.run_config?.freq_options?.day}
      />
      <FormErrorMessage fontSize={12}>
        {form.errors.run_config?.freq_options?.day}
      </FormErrorMessage>
    </FormControl>
  );
}

function MonthPicker({ form }: { form: FormType }) {
  const options = [
    { value: "first_of_the_month", label: "First of Month" },
    { value: "last_of_month", label: "Last of Month" },
    { value: "custom", label: "Custom" },
  ];

  return (
    <>
      <FormControl
        isInvalid={!!form.errors.run_config?.freq_options?.date_string}
      >
        <FormLabel color="gray.500" fontSize="xs">
          Run every
        </FormLabel>
        <DropdownWithSearch
          options={options}
          value={options.find(
            (item) =>
              item.value === form.values.run_config.freq_options.date_string
          )}
          onChange={(option) =>
            form.setFieldValue(
              "run_config.freq_options.date_string",
              option?.value ?? ""
            )
          }
          isInvalid={!!form.errors.run_config?.freq_options?.date_string}
        />
        <FormErrorMessage fontSize={12}>
          {form.errors.run_config?.freq_options?.date_string}
        </FormErrorMessage>
      </FormControl>
      {form.values.run_config.freq_options.date_string === "custom" && (
        <InputFieldWithError
          labelText="Day of Month"
          value={form.values.run_config.freq_options.day || ""}
          errorMsg={form.errors.run_config?.freq_options?.day}
          formLabelProps={{ color: "gray.500", fontSize: "xs" }}
          name="run_config.freq_options.day"
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          type="number"
          max="31"
          step="1"
          min="1"
        />
      )}
    </>
  );
}

function FormWrapper({ children }: { children: any }) {
  return (
    <>
      <VStack alignItems="left" spacing="2">
        {children}
      </VStack>
    </>
  );
}

export function ScheduleOnce({ form }: { form: FormType }) {
  return (
    <FormWrapper>
      <StartDateInput form={form} />
      <TimeInput form={form} />
    </FormWrapper>
  );
}

export function SchedulePeriodic({ form }: { form: FormType }) {
  const invalidFrequency = !!form.errors.run_config?.frequency;
  const invalidUnit = !!form.errors.run_config?.unit;
  const options = Object.values(PERIODIC_FREQUENCIES).map(
    (freq: PERIODIC_FREQUENCIES) => {
      return { label: capitalize(freq), value: freq };
    }
  );

  return (
    <FormWrapper>
      <StartDateInput form={form} />
      <EndDateInput form={form} />
      <HStack spacing="3" alignItems="flex-end">
        <InputFieldWithError
          labelText="Run every"
          value={form.values.run_config.unit ?? undefined}
          errorMsg={form.errors.run_config?.unit}
          formLabelProps={{ color: "gray.500", fontSize: "xs" }}
          name="run_config.unit"
          onChange={form.handleChange}
          onBlur={form.handleBlur}
          type="number"
          min="1"
          // add bottom margin to matain both fields in same horizontal line when there's a valdtion error message
          mb={invalidFrequency && !invalidUnit ? "26px" : undefined}
        />
        <FormControl isInvalid={invalidFrequency} py="8px">
          <DropdownWithSearch
            options={options}
            value={options.find(
              (item) => item.value === form.values.run_config.frequency
            )}
            onChange={(option) =>
              form.setFieldValue("run_config.frequency", option?.value ?? "")
            }
            isInvalid={invalidFrequency}
            // adding bottom margin to matain both fields in same horizontal line when there's a valdtion error message
            controlStyle={{
              marginBottom:
                !invalidFrequency && invalidUnit ? "26px" : undefined,
            }}
          />
          <FormErrorMessage fontSize={12}>
            {form.errors.run_config?.frequency}
          </FormErrorMessage>
        </FormControl>
      </HStack>
      <TimeInput form={form} />
    </FormWrapper>
  );
}

export function ScheduleRecurring({ form }: { form: FormType }) {
  function frequencyFormSwitch(frequency: RECURRING_FREQUENCIES) {
    switch (frequency) {
      case RECURRING_FREQUENCIES.WEEK:
        return <WeekPicker form={form} />;
      case RECURRING_FREQUENCIES.MONTH:
        return <MonthPicker form={form} />;
      case RECURRING_FREQUENCIES.YEAR:
        return (
          <InputFieldWithError
            labelText="Run every"
            value={form.values.run_config.freq_options.date || ""}
            errorMsg={form.errors.run_config?.freq_options?.date}
            name="run_config.freq_options.date"
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            type="date"
            formLabelProps={{ color: "gray.500", fontSize: "xs" }}
          />
        );
      case RECURRING_FREQUENCIES.DAY:
        return (
          <InputFieldWithError
            labelText="Run every"
            value={form.values.run_config.freq_options.unit}
            errorMsg={form.errors.run_config?.freq_options?.unit}
            name="run_config.freq_options.unit"
            onChange={form.handleChange}
            onBlur={form.handleBlur}
            type="number"
            formLabelProps={{ color: "gray.500", fontSize: "xs" }}
          />
        );
    }
  }

  const options = Object.values(RECURRING_FREQUENCIES).map((freq) => {
    return { label: capitalize(freq), value: freq };
  });

  return (
    <FormWrapper>
      <StartDateInput form={form} />
      <EndDateInput form={form} />
      <TimeInput form={form} />

      <FormControl isInvalid={!!form.errors.run_config?.frequency}>
        <FormLabel color="gray.500" fontSize="xs">
          <ITitle title="Schedule frequency" />
        </FormLabel>
        <DropdownWithSearch
          options={options}
          value={options.find(
            (item) => item.value === form.values.run_config.frequency
          )}
          onChange={(option) =>
            form.setFieldValue("run_config.frequency", option?.value ?? "")
          }
          isInvalid={!!form.errors.run_config?.frequency}
        />
        <FormErrorMessage fontSize={12}>
          {form.errors.run_config?.frequency}
        </FormErrorMessage>
      </FormControl>

      {frequencyFormSwitch(
        form.values.run_config.frequency as RECURRING_FREQUENCIES
      )}
    </FormWrapper>
  );
}
